import { ReactElement, MouseEvent } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import classNames from 'classnames';
import { SUB_ACTIVE_STATUSES, SUB_CANCELED_STATUSES } from '@localstack/constants';
import { computeNumLicensesUsed, formatDate, formatMonetaryAmount } from '@localstack/services';

import {
  Subscription,
  PlanFamily,
  LicenseAssignment,
  ProductFeature,
  FeatureTuple,
  EmulatorType,
} from '@localstack/types';

import { Link, Table, TableHead, TableRow, TableCell, TableBody, Box } from '@mui/material';

import { getPeriodEnd } from '@localstack/ui';

import { SubscriptionStatus } from '../../../display/SubscriptionStatus';
import { EmulatorPills } from '../EmulatorPills';

export type SubscriptionFeatureResponse = {
  default: FeatureTuple[];
  add: FeatureTuple[];
  remove: FeatureTuple[];
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    table: {
      '& > *': {
        textWrap: 'nowrap',
      },
    },

    trialRow: {
      background: `linear-gradient(-45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
      '& td:first-child': {
        paddingLeft: `${theme.spacing(1)} !important`,
      },
      '& td, & td a': {
        color: theme.palette.primary.contrastText,
      },
    },
  }),
);

/** The emulators a subscription gives access to: its own, plus Snowflake when the plan grants that feature. */
const getSubscriptionEmulators = (
  subscription: Subscription,
  features?: SubscriptionFeatureResponse,
): EmulatorType[] => {
  const { emulator } = subscription.plan;
  const bundlesSnowflake = emulator !== EmulatorType.SNOWFLAKE && computeHasSnowflake(features);
  return bundlesSnowflake ? [emulator, EmulatorType.SNOWFLAKE] : [emulator];
};

const computeHasSnowflake = (features?: SubscriptionFeatureResponse): boolean => {
  if (!features) return false;

  const combined = new Set<ProductFeature>([
    ...features.default.map(([feature]) => feature),
    ...features.add.map(([feature]) => feature),
  ]);

  for (const [feature] of features.remove) {
    combined.delete(feature);
  }

  return combined.has(ProductFeature.FEATURE_SNOWFLAKE);
};

export interface SubscriptionsListProps {
  showCancelled?: boolean;
  subscriptions: Subscription[];
  licenseAssignments: LicenseAssignment[];
  onViewSubscription?: (subscription: Subscription) => unknown;
  isAdminPanel?: boolean;
  subscriptionFeatures: Record<string, SubscriptionFeatureResponse>;
}

export const SubscriptionsList = ({
  showCancelled,
  subscriptions,
  licenseAssignments,
  onViewSubscription,
  isAdminPanel,
  subscriptionFeatures,
}: SubscriptionsListProps): ReactElement => {
  const classes = useStyles();

  const activeSubs: Subscription[] = subscriptions.filter(({ status }) => SUB_ACTIVE_STATUSES.includes(status));
  const cancelledSubs = subscriptions.filter(({ status }) => SUB_CANCELED_STATUSES.includes(status));

  const subscriptionsToShow: Subscription[] = [
    ...(showCancelled ? [...activeSubs, ...cancelledSubs] : [...activeSubs]),
  ].sort((subA, subB) => subB.start_date - subA.start_date);

  const licensesUsed = (subscription?: Subscription): number | undefined => {
    if (!subscription || !licenseAssignments) {
      return undefined;
    }

    return computeNumLicensesUsed(subscription, licenseAssignments);
  };

  return (
    <Box style={{ overflowX: 'auto' }}>
      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>Subscription Plan</TableCell>
            {isAdminPanel && <TableCell>ID</TableCell>}
            <TableCell>Renewal Date</TableCell>
            <TableCell>Est. Renewal Cost (Excl. VAT)</TableCell>
            <TableCell>Licenses Used</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {subscriptionsToShow.map((subscription) => (
            <TableRow
              hover
              key={subscription.id}
              className={classNames(`plan-${subscription.plan.family}`, {
                [classes.trialRow]: subscription.plan?.family === PlanFamily.TRIAL_PLANS,
              })}
            >
              <TableCell>
                <Box display="inline-flex" alignItems="center" gap="4px">
                  <Link
                    href="#"
                    onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                      e.preventDefault();
                      onViewSubscription?.(subscription);
                    }}
                    underline="hover"
                    sx={{ color: 'primary.light' }}
                  >
                    {subscription.name || subscription.plan.name || ''}
                  </Link>
                  <EmulatorPills
                    emulators={getSubscriptionEmulators(subscription, subscriptionFeatures?.[subscription.id])}
                  />
                </Box>
              </TableCell>

              {isAdminPanel && (
                <TableCell>
                  <code>{subscription.id}</code>
                </TableCell>
              )}

              <TableCell>
                {subscription.cancel_at_period_end || subscription.cancel_at
                  ? '-'
                  : formatDate(getPeriodEnd(subscription))}
              </TableCell>
              <TableCell>
                {formatMonetaryAmount(
                  (subscription.estimated_mrr ?? 0) * subscription.interval_months,
                  subscription.currency ?? 'usd',
                )}
                {' / '}
                {(subscription.interval_months || 1) === 12 ? 'year' : 'month'}
              </TableCell>
              <TableCell>
                {licensesUsed(subscription)} of {subscription.seats}
              </TableCell>
              <TableCell>
                <SubscriptionStatus subscription={subscription} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
